/**
 * TransactionDateRangeControl [T-WP2] [R50]
 *
 * Transaction-local unified date-range control. Replaces the two duplicated
 * date branches in `UnifiedFilterBar.tsx` (the expanded inline period pills +
 * the `minimized-inline` popover) with a single self-contained control that
 * owns:
 *
 *   - explicit range trigger with stable test IDs
 *   - calendar-first date surface with grouped quick-range presets
 *   - Custom range with local popover draft state
 *   - Apply-only commit semantics (first calendar click does NOT change the
 *     committed state; Cancel/Escape/outside dismissal discards the draft)
 *   - accessible Start/End slots with stable IDs
 *
 * The control is presentation-aware: `'expanded'` is the desktop/full
 * presentation; `'minimized'` is the floating-bar (collapsed topbar) inline
 * presentation. The parent (`UnifiedFilterBar`) is responsible for translating
 * its public `presentation: 'full' | 'minimized-inline'` values into the
 * `expanded | minimized` values this component expects.
 *
 * Reuses the shared `Calendar` (`mode="range"`) primitive — no edits to
 * `calendar.tsx` or any other shared primitive.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button, buttonVariants } from '@/components/ui/button';
import { CalendarIcon, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import {
  PERIOD_LABELS,
  getDateRangeFromFilter,
  type FilterState,
  type PeriodLiteral,
} from '@/lib/filters';
import { useScrollEdgeState } from '@/hooks/useScrollEdgeState';
export type DateRangePresentation = 'expanded' | 'minimized';

export interface TransactionDateRangeControlProps {
  /** Committed filter state. The control is fully controlled — it does not mutate this directly. */
  value: FilterState;
  /**
   * Single commit point for final state transitions. Called when:
   *   - the user picks a non-Custom preset (the parent may route through a
   *     wrapper that performs its own period change semantics — see
   *     `onPresetCommit` below)
   *   - the user clicks Apply in the Custom range view (commits a fully-formed
   *     `{ period: 'custom', dateFrom, dateTo }` shape)
   *
   * Apply-only: NEVER called on a single calendar click. Calendar selections
   * update local draft state only.
   */
  onCommit: (next: FilterState) => void;
  /**
   * Optional override for preset commits. When provided, the control calls
   * this instead of computing its own `next` state, so the parent can preserve
   * custom→preset undo toast semantics (see `UnifiedFilterBar.handlePeriodChange`).
   */
  onPresetCommit?: (period: PeriodLiteral) => void;
  /**
   * Real available transaction date bounds, computed from the loaded
   * transaction list. Surfaced on the Start/End cards AND on the trigger when
   * the committed period is `all`, so the UI never fabricates an endpoint.
   * Falls back to the existing `All dates` placeholder when omitted.
   */
  transactionDateBounds?: { from?: Date; to?: Date };
  /**
   * [AC5] Optional container/anchor measurements. When provided, the popover
   * aligns its left edge to `containerLeft` (via `alignOffset`) and uses
   * `containerWidth` for its width (clamped to `viewportWidth - 16` for
   * safety). This makes the popover align with the owning transaction
   * filter-card section, giving the calendar usable width on narrow screens.
   * Falls back to the existing
   * `w-[min(48rem,calc(100vw-2rem))]` default when omitted (legacy behavior).
   */
  popoverContainerMetrics?: { left: number; width: number };
  presentation: DateRangePresentation;
}

/**
 * Quick-range groups rendered beside the calendar. Legacy `7d`/`30d`
 * values remain available for persisted URLs while the calendar stays visible
 * as the primary date-selection surface.
 */
const PRESET_GROUPS = [
  { label: 'Short range', periods: ['1d', '2d', '3d', '5d', '7d', '1w', '2w'] },
  { label: 'Long range', periods: ['1m', '3m', '6m', '30d', '1y'] },
] as const satisfies readonly { label: string; periods: readonly PeriodLiteral[] }[];

const MONTH_DAY_YEAR = 'MMM d, yyyy';
const MONTH_DAY = 'MMM d';
// [AC7] All-range date surface uses day name + month name + year so the user
// can read the absolute available range at a glance. `EEE` = 'Mon', `MMM` =
// 'Jan', `yyyy` = '2026'. Kept separate from MONTH_DAY_YEAR so other surfaces
// (preset chips, custom commit label) keep the compact MMM d, yyyy form.
const ALL_RANGE_DATE = 'EEE MMM d, yyyy';
// [T-RAIL-SIDEBAR-EDGE] `all` has no boundaries when no real bounds are available.
// Every surface that would show a date for a bounded period shows this literal
// instead, so no indicator ever fabricates an endpoint or implies an API range
// that does not exist.
const ALL_DATES_LABEL = 'All dates';

export function TransactionDateRangeControl({
value,
onCommit,
onPresetCommit,
presentation,
  transactionDateBounds,
  popoverContainerMetrics,
}: TransactionDateRangeControlProps) {
  const isMinimized = presentation === 'minimized';
  const [open, setOpen] = useState(false);
  // Local draft state. Initialized from `value` whenever the popover opens or
  // the user enters the Custom view. Discarded on any close path.
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  // [AC5] Trigger button ref — used to compute `alignOffset` so the popover
  // aligns its left edge with the owning filter-card section, not the trigger.
  // Captured at popover-open time in handleOpenChange.
  const triggerRef = useRef<HTMLButtonElement>(null);
  // [AC5] Popover layout snapshot — captures the offset (trigger.left → card.left)
  // and the clamped width (min(card.width, viewport - 16)) in the open-state
  // effect after the trigger is mounted. `null` while closed; cleared by
  // handleOpenChange on close.
  const [popoverLayout, setPopoverLayout] = useState<{ offset: number; width: number } | null>(null);
  // [WP2-D2] Track whether the user has made at least one calendar selection
  // in the current Custom view session. react-day-picker v9 with default min=0
  // returns `{from: X, to: X}` on the FIRST click of an empty range (see
  // node_modules/react-day-picker/dist/esm/utils/addToRange.js:21), which would
  // otherwise satisfy `!!draft?.from && !!draft?.to` and enable Apply after a
  // single Start click. We force the first event to land as `from` only so the
  // user must make a second distinct selection before Apply unlocks.
  const isFirstSelectionRef = useRef<boolean>(true);
  // [RANGE_VISUAL_ITERATION_6] Ref to whichever preset/All/Custom button is the
  // currently committed period. The preset rail is scrollable on mobile (the
  // "Long range" group sits below the initial rail height) and on desktop at
  // narrow sidebars, so when the popover opens the active chip can be hidden. We
  // scroll it into view WITHOUT moving focus and WITHOUT swallowing click/keyboard
  // semantics — `scrollIntoView` only adjusts the scroll position of ancestors.
  const activePresetRef = useRef<HTMLButtonElement | null>(null);
  // [RANGE_VISUAL_ITERATION_31] The calendar wrapper's 40px bottom fade is a
  // scroll affordance, but it was hard-coded via `after:content-['']` and so
  // painted in states where the grid does not scroll at all. Cycle-31 measured 5
  // of 6 states (390 committed, 768 committed+draft, 1440 committed+draft) with
  // scrollHeight === clientHeight while the `card→transparent` band still veiled
  // 21-27px of the fully-visible final week row's 36px height (Aug 30, Aug 31 +
  // the Sep 1-5 outside days). Iterations 28 and 30 shrank the week-row gap
  // (mt-2 → mt-1 → mt-0) until the 6-row grid fit without scrolling, which turned
  // the affordance into a false "more below" signal plus a contrast loss on real
  // interactive day buttons. `calendarHasMoreBelow` replaces that assumption with
  // a measurement, so the fade only renders where scroll genuinely remains (390
  // draft, where the draft banner shrinks the flex-1 wrapper by 18px, and any
  // cramped viewport height that shrinks it further). The scrollport node is held
  // in state, not a ref, because the effect below must re-run when Radix mounts
  // it — see the timing note there.
  const [calendarScrollEl, setCalendarScrollEl] = useState<HTMLDivElement | null>(null);
  const [calendarHasMoreBelow, setCalendarHasMoreBelow] = useState(false);
  // [T-CalendarRail-Redesign] Shared scroll-edge affordance — drives the rail
  // fades from measured overflow state so we never paint a "more above" cue
  // over fully-visible content. Calendar's own bottom-fade remains independent
  // (calendarHasMoreBelow above) since the rail and calendar scroll independently.
  //
  // [T-RAIL-SIDEBAR-EDGE] `wrapperRef` attaches the fade to a NON-SCROLLING
  // wrapper around the scrollport. The previous single-element form painted the
  // pseudo-elements inside the scrollport, whose absolute containing block
  // scrolls with the content: a `top: 0` probe in this rail measured -91px drift
  // at scrollTop 91 and -182px at scrollTop 182 (390x844), which is why the cue
  // read as a blur floating over content instead of an edge boundary.
  const { ref: railScrollEdgeRef, wrapperRef: railScrollEdgeWrapperRef } = useScrollEdgeState<
    HTMLDivElement,
    HTMLDivElement
  >();
  // Reset the draft whenever the popover opens or the committed dates change
  // while open. The init captures the latest committed value so the user can
  // pick up where they left off after closing/reopening.
  useEffect(() => {
    if (open) {
      const initial: DateRange | undefined =
        value.dateFrom || value.dateTo
          ? { from: value.dateFrom, to: value.dateTo }
          : undefined;
      setDraft(initial);
      // A persisted complete range counts as a completed selection (the user
      // already committed both ends); an empty persisted Custom entry (rare
      // partial state) starts the next session at "no selection yet".
      isFirstSelectionRef.current = !(initial?.from && initial?.to);
    }
  }, [open, value.dateFrom, value.dateTo]);

  // [RANGE_VISUAL_ITERATION_6] After the popover opens (or the committed period
  // changes while open), nudge the active preset chip into view inside the preset
  // rail. `block:'nearest'` keeps the rail scroll untouched if the chip is already
  // visible; rAF defers one frame so any entrance animation/portal mount has settled.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      activePresetRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [open, value.period]);

  // [RANGE_VISUAL_ITERATION_31] Keep the calendar's bottom fade honest by driving
  // it off MEASURED remaining scroll instead of hard-coding it. Wired through a
  // CALLBACK ref, not `useRef` + a `[open]` effect: cycle-31 instrumentation
  // proved Radix mounts `PopoverContent` in a later commit than the `open` state
  // change, so a `[open]`-keyed effect reads `ref.current === null` and never
  // attaches (the iter-6 effect above dodges the same timing with rAF). Keying
  // the effect on the node itself attaches exactly when the scrollport mounts and
  // detaches when the popover closes. ResizeObserver delivers an initial callback
  // on observe(), which also covers the frame where Radix has not yet resolved
  // `--radix-popover-content-available-height`; the scroll listener retires the
  // fade once the user actually reaches the bottom. The grid element is observed
  // too, because a 5-row/6-row month change alters scrollHeight without changing
  // the wrapper's own box. Read-only measurement — no layout is written, so the
  // calendar geometry, 44px targets, Apply-only commit, and every selector are
  // untouched.
  useEffect(() => {
    const el = calendarScrollEl;
    if (!el) {
      setCalendarHasMoreBelow(false);
      return;
    }
    const update = () => {
      setCalendarHasMoreBelow(el.scrollHeight - el.clientHeight - el.scrollTop > 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [calendarScrollEl]);
  // [RADIX-RESIZE-001] Shared popover-layout measurement. Computes the offset
  // (shifts Radix `align="start"` from the trigger's left edge to the owning
  // filter-card's left edge) and the clamped width (`min(card.width, viewport - 16)`)
  // from the live trigger geometry + the parent-supplied card metrics. Called
  // both at open time AND whenever the trigger reflows, the container metrics
  // change, or the viewport resizes — see the effect below. Returns null when
  // either input is missing (Radix hasn't mounted the trigger yet, or the
  // parent didn't pass metrics — both rare and the popover falls back to its
  // legacy CSS-class width clamp at the call site).
  const computePopoverLayout = useCallback(
    (): { offset: number; width: number } | null => {
      if (!triggerRef.current || !popoverContainerMetrics) return null;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const maxWidth = window.innerWidth - 16;
      return {
        offset: popoverContainerMetrics.left - triggerRect.left,
        width: Math.min(popoverContainerMetrics.width, maxWidth),
      };
    },
    [popoverContainerMetrics],
  );

  // [RADIX-RESIZE-001] Recompute `popoverLayout` while the popover is open:
  //   1. On open — initial rAF capture (defers one frame for Radix mount +
  //      layout settle so the first measurement isn't against an unmounted
  //      trigger).
  //   2. On trigger reflow — ResizeObserver on `triggerRef.current` fires when
  //      the trigger's box changes (parent column resize, label wrap, draft
  //      dot toggling on/off).
  //   3. On viewport resize — `window` resize listener fires for orientation
  //      change, devtools toggle, snap resize, and any layout-level width
  //      shift. The `innerWidth - 16` clamp in `computePopoverLayout` keeps
  //      the popover inside the viewport at every resize tick.
  //   4. On `popoverContainerMetrics` change — the parent recomputed the row
  //      metrics (e.g. sidebar collapse), so `computePopoverLayout`'s
  //      dependency identity flips and the effect re-attaches with the new
  //      inputs. Same ResizeObserver + window-listener cleanup runs to
  //      guarantee no listener leaks.
  // The cleanup runs in LIFO order so a fast open→close→open cycle never
  // accumulates observers or `resize` handlers (verified via
  // `cleanup = () => { cancelAnimationFrame(...); observer?.disconnect();
  // window.removeEventListener('resize', onWindowResize); }`). Stable
  // dependency list avoids Rules-of-Hooks violations: `[open, computePopoverLayout]`.
  useEffect(() => {
    if (!open) return;
    const initialId = requestAnimationFrame(() => {
      const layout = computePopoverLayout();
      if (layout) setPopoverLayout(layout);
    });
    const triggerEl = triggerRef.current;
    const observer = triggerEl ? new ResizeObserver(() => {
      const layout = computePopoverLayout();
      if (layout) setPopoverLayout(layout);
    }) : null;
    if (observer && triggerEl) observer.observe(triggerEl);
    const onWindowResize = () => {
      const layout = computePopoverLayout();
      if (layout) setPopoverLayout(layout);
    };
    window.addEventListener('resize', onWindowResize);
    return () => {
      cancelAnimationFrame(initialId);
      observer?.disconnect();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [open, computePopoverLayout]);

  // Discard draft on any close path. Radix fires `onOpenChange(false)` for
  // Escape, outside click, and trigger toggle — all of which should drop the
  // draft without committing. The first-selection guard is also reset so a
  // re-opened popover starts a fresh Custom session. Layout is cleared on
  // close so the next open path recomputes from the freshest geometry
  // (the [RADIX-RESIZE-001] effect above handles the initial open-time
  // capture; this branch owns the close-time cleanup).
  const handleOpenChange = useCallback((next: boolean) => {
    if (!next) {
      setDraft(undefined);
      isFirstSelectionRef.current = true;
      setPopoverLayout(null);
    }
    setOpen(next);
  }, []);

  // ── Preset commit ──────────────────────────────────────────────────────────
  const handlePresetClick = useCallback(
    (period: PeriodLiteral) => {
      if (period === 'custom') {
        // The calendar is already the primary surface; Custom is a semantic
        // selection affordance and must not commit until Apply is pressed.
        return;
      }
      if (onPresetCommit) {
        // Route through the parent so the existing `handlePeriodChange`
        // undo-toast semantics survive (custom→preset replacement toast).
        onPresetCommit(period);
      } else {
        // Fallback: compute the next state locally. Mirrors the previous
        // `handleDateRangeSelect` contract — strip `dateFrom`/`dateTo` for
        // non-custom periods. TS has narrowed `period` to exclude 'custom'
        // because the early-return at the top of this handler removed it.
        const next: FilterState = { ...value, period };
        delete next.dateFrom;
        delete next.dateTo;
        onCommit(next);
      }
      setOpen(false);
    },
    [onCommit, onPresetCommit, value],
  );

// ── Custom range draft handlers ────────────────────────────────────────────
// The first click on a day sets only `from` (Start). Selecting `to` (End)
// completes the range. Either way, we do NOT call onCommit here — the
// range lives in `draft` until the user clicks Apply.
//
// [WP2-D2] react-day-picker v9 returns `{from: X, to: X}` for the FIRST click
// in an empty range when `min={0}` (default). We strip `to` on the first event
// so the user must make a second distinct selection before Apply unlocks.
const handleCalendarSelect = useCallback((range: DateRange | undefined) => {
if (isFirstSelectionRef.current && range?.from) {
isFirstSelectionRef.current = false;
setDraft({ from: range.from, to: undefined });
return;
}
if (!range?.from && !range?.to) {
// react-day-picker cleared the range (e.g. user clicked the active
// start with required=false). Allow the next click to be a fresh Start.
isFirstSelectionRef.current = true;
}
setDraft(range);
}, []);

  const canApply = !!(draft?.from && draft?.to);


  const handleApply = useCallback(() => {
    if (!draft?.from || !draft?.to) return;
    onCommit({
      ...value,
      period: 'custom',
      dateFrom: draft.from,
      dateTo: draft.to,
    });
    setOpen(false);
  }, [draft, onCommit, value]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setDraft(undefined);
  }, []);

  // ── Trigger presentation ──────────────────────────────────────
  // [WP2-D4] Derive the actual Start/End boundaries from the canonical period
  // helper (`getDateRangeFromFilter`) so every preset AND every custom value
  // exposes the same range contract. Custom without stored dates (a partial
  // persisted state — see useFilterUrlSync/filtersFromQueryString) is
  // surfaced explicitly as "Custom · pick a range" rather than a silent empty.
  const resolvedRange = useMemo(() => {
    if (value.period === 'custom') {
      return { from: value.dateFrom, to: value.dateTo };
    }
    if (value.period === 'all') {
      return { from: undefined, to: undefined };
    }
    const iso = getDateRangeFromFilter(value.period, value.dateFrom, value.dateTo);
    return {
      from: iso.dateFrom ? new Date(iso.dateFrom) : undefined,
      to: iso.dateTo ? new Date(iso.dateTo) : undefined,
    };
  }, [value.period, value.dateFrom, value.dateTo]);

  // [RANGE_VISUAL_ITERATION_17] Start/End summary cards surface whichever range is currently
  // meaningful: an in-flight Custom draft when one exists, otherwise the resolved range.
  // This gives preset chips (30D, 1M, etc.) a populated Start/End pair matching the trigger,
  // while `all` and a partial Custom stay truthfully empty. Apply remains gated by the draft.
  const displayRange = draft ?? resolvedRange;
  // [T-RAIL-SIDEBAR-EDGE] `all` truly has no endpoints, so the Start/End cards say
  // exactly that instead of prompting for dates the committed filter never uses.
  // An in-flight draft always wins (its dates are real), so this flips to false
  // the moment the user picks a day.
  const isAllRange = value.period === 'all' && !draft;
  // [AC7] Real transaction date bounds, surfaced on the Start/End cards AND on
  // the trigger when the committed period is `all`. Falls back to undefined when
  // the parent has not supplied bounds (e.g. transactions still loading), so the
  // Start/End cards stay truthful and never fabricate a date. Both bounds must
  // be present to be meaningful — a half-open range still gets the ALL_DATES_LABEL.
  const allRangeDates = useMemo(() => {
    if (!isAllRange) return undefined;
    const { from, to } = transactionDateBounds ?? {};
    if (!from || !to) return undefined;
    return { from, to };
  }, [isAllRange, transactionDateBounds]);

  // [RANGE_VISUAL_ITERATION_18] True only when the user has built a complete draft
  // that diverges from the currently committed range. Mirrors iter17's draft-first
  // displayRange contract: when the draft equals the committed range, no banner is
  // shown (the user has simply re-selected the same dates). When the draft differs,
  // we surface an unmistakable "Draft range" pill + a small dot on the trigger so
  // the committed 30D chip/trigger cannot be mistaken for an in-flight Custom draft.
  // Apply remains the only commit path; the indicators disappear the moment the
  // popover closes (draft discarded) or Apply commits.
  const hasDraft = useMemo(() => {
    if (!canApply || !draft?.from || !draft?.to) return false;
    const rFrom = resolvedRange.from?.getTime();
    const rTo = resolvedRange.to?.getTime();
    const dFrom = draft.from.getTime();
    const dTo = draft.to.getTime();
    return dFrom !== rFrom || dTo !== rTo;
  }, [canApply, draft, resolvedRange.from, resolvedRange.to]);


  const triggerLabel = useMemo(() => {
    // [RANGE_VISUAL_ITERATION_20] When a complete draft diverges from the committed range
    // (per iter-18's hasDraft contract), surface the in-flight custom dates on the trigger
    // so the committed 30D chip/trigger cannot be mistaken for an in-flight Aug 10–Aug 20
    // draft. The amber dot already signals the pending state; pairing it with the draft
    // dates makes the in-flight range unmistakable across every viewport. Apply-only commit
    // semantics are preserved: closing the popover drops the draft and the trigger reverts
    // to the committed label immediately, no partial commits.
    // [T-RAIL-SIDEBAR-EDGE] The roomy (non-minimized) label now carries the full
    // `MMM d, yyyy` form — `Custom · Aug 22, 2026 – Aug 30, 2026` — so a committed
    // custom range names its period AND both endpoints including the year. The
    // minimized inline trigger uses an intrinsic 44px-floor slot inside the floating
    // bar, so it keeps the compact `MMM d` form; the year stays reachable there via
    // the Start/End cards and the aria-label below.
    const dateFormat = isMinimized ? MONTH_DAY : MONTH_DAY_YEAR;
    if (hasDraft && displayRange?.from && displayRange?.to) {
      const fromStr = format(displayRange.from, dateFormat);
      const toStr = format(displayRange.to, dateFormat);
      return isMinimized ? `${fromStr} – ${toStr}` : `Custom · ${fromStr} – ${toStr}`;
    }
    const periodLabel = PERIOD_LABELS[value.period] ?? 'Custom';
    // `all` states its endpoint semantics instead of wearing a date-shaped label.
    if (value.period === 'all') return `${periodLabel} · ${ALL_DATES_LABEL}`;
    const { from, to } = resolvedRange;
    if (!from) {
      // Custom without stored dates (or partial persisted state). The trigger
      // should not pretend to know a range.
      return isMinimized ? 'Custom' : 'Custom · pick a range';
    }
    const fromStr = format(from, dateFormat);
    const toStr = to ? ` – ${format(to, dateFormat)}` : '';
    return isMinimized
      ? `${fromStr}${toStr}`
      : `${periodLabel} · ${fromStr}${toStr}`;
  }, [value.period, resolvedRange, isMinimized, hasDraft, displayRange]);
  // [FOLLOW-UP-MOBILE-INDICATOR] Mobile (<lg) label mirrors the desktop triggerLabel
  // state machine for custom/all/incomplete-custom, but stays compact for preset
  // chips. The 44px-min touch target grows to fit the wrapped label up to a safe
  // `max-w-[8rem]` (128px); `compactTriggerLabel` carries the full `MMM d, yyyy`
  // form so the mobile surface names the period AND both endpoints (or the
  // truthful absence of endpoints for `all` / incomplete custom). Preset labels
  // stay as the bare `PERIOD_LABELS` so the 1D/3D/30D chips remain visually
  // compact. Desktop lg span + every data-testid/aria-label selector is
  // preserved (triggerAriaLabel above uses MONTH_DAY_YEAR for any range-backed
  // period, and the test suite at testify/e2e/r50-transactions-toolbar.spec.ts
  // asserts only on aria-label/click, not on the visible label text).
  const compactTriggerLabel = useMemo(() => {
    const periodLabel = PERIOD_LABELS[value.period] ?? 'Custom';
    if (value.period === 'all') {
      return `${periodLabel} · ${ALL_DATES_LABEL}`;
    }
    if (value.period === 'custom') {
      const { from, to } = resolvedRange;
      if (!from || !to) {
        return 'Custom · pick a range';
      }
      return `Custom · ${format(from, MONTH_DAY_YEAR)} – ${format(to, MONTH_DAY_YEAR)}`;
    }
    return periodLabel;
  }, [value.period, resolvedRange]);

  // [WP2-D4] aria-label must always include Start and End labels and actual
  // values for any range-backed period. Test R50 asserts `/Start/`, `/End/`,
  // and `/Aug/` on a persisted custom URL — that contract is only met when
  // the resolved boundaries are surfaced even if value.dateFrom/dateTo are
  // absent (preset periods derive boundaries from getDateRangeFromFilter).
  const triggerAriaLabel = useMemo(() => {
    const periodLabel = PERIOD_LABELS[value.period] ?? 'Custom';
    if (value.period === 'all') {
      return 'Date range: All transactions. Start: all dates. End: all dates. No fixed boundaries.';
    }
    const { from, to } = resolvedRange;
    const startStr = from ? format(from, MONTH_DAY_YEAR) : 'no start date';
    const endStr = to ? format(to, MONTH_DAY_YEAR) : 'no end date';
    return `Date range: ${periodLabel}. Start ${startStr}. End ${endStr}.`;
  }, [value.period, resolvedRange]);

  // ── Stable test IDs ────────────────────────────────────────────────────────
  const triggerTestId = isMinimized
    ? 'minimized-date-range-trigger'
    : 'transaction-date-range-trigger';
  const presetTestIdPrefix = isMinimized
    ? 'minimized-date-preset-'
    : 'period-pill-';
  const customTestId = isMinimized
    ? 'minimized-date-preset-custom'
    : 'period-pill-custom';

  return (
    <div data-testid="transaction-date-range-control" className="contents">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant={value.period === 'custom' ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'min-h-[44px] min-w-[44px] gap-0 text-[10px] font-medium',
              isMinimized
                // [FOLLOW-UP-MOBILE-INDICATOR] Width now grows with content (basis:auto) up to a safe
                // 128px (`max-w-[8rem]`) so wrapped `Custom · Aug 22, 2026 – Aug 30, 2026`
                // fits on 2 lines without horizontal document overflow. The 44px floor
                // (`min-w-[44px]`, matching `min-h-[44px]`) keeps short presets aligned while
                // `flex-[0_1_auto]` lets longer labels claim only their intrinsic space.
                // `px-1` gives wrapped labels 4px of horizontal breathing room against the icon.
                ? 'h-11 min-h-[44px] min-w-[44px] max-w-[8rem] flex-[0_1_auto] shrink px-1 lg:gap-1 lg:px-2 lg:text-xs'
                : 'h-9 px-3 text-xs md:min-h-0 md:min-w-0',
              value.period === 'custom'
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-card text-card-foreground',
            )}
            data-testid={triggerTestId}
            aria-label={triggerAriaLabel}
          >
            <CalendarIcon
              className={cn(
                'shrink-0',
                isMinimized ? 'h-2.5 w-2.5' : 'h-3 w-3',
                isMinimized ? 'mr-0' : 'mr-1',
              )}
            />
            {/* [RANGE_VISUAL_ITERATION_24] Mirror the minimized-mode compact/full pattern on the expanded-mode trigger so mobile (<lg) shows the period label alone (e.g. `30D`, `Custom`, `All`) instead of the half-truncated `30D · Jul 31,…` the cycle-7 independent visual review flagged. Full date label still renders at lg+ where the inline filter row has room. Preserves 30D-active-when-committed, Apply-only commit, and all existing test selectors. */}
            {/* [FOLLOW-UP-MOBILE-INDICATOR] Mobile span wraps over 2-3 lines at 10px so the full
                `Custom · Aug 22, 2026 – Aug 30, 2026` (or `All · All dates`, or
                `Custom · pick a range`) is visible inside the 44px-min touch target. Removed
                `whitespace-nowrap` + `shrink-0` per the packet — nowrap would clip the new
                custom/all state; shrink-0 would prevent the wrapped lines from claiming their
                height. `min-w-0` lets the flex item shrink; `break-words` (alias of
                `overflow-wrap-anywhere`) wraps on any token so long date ranges break cleanly
                inside the 128px max button width. `text-[10px] leading-tight` keeps the two/three
                line stack within the 44px button height (measured ~13px per line × 2 = 26px,
                plus 8px vertical padding = 34px, leaving headroom for the CalendarIcon). The
                `lg:hidden` breakpoint is preserved — desktop lg still uses triggerLabel
                (month-day-only format) untouched. */}
            <span className="min-w-0 break-words !whitespace-normal !overflow-visible !text-clip text-[10px] leading-tight lg:hidden">{compactTriggerLabel}</span>
            <span className="hidden shrink-0 whitespace-nowrap lg:inline">{triggerLabel}</span>
            {/* [RANGE_VISUAL_ITERATION_18] Draft-pending dot. Pinned to the trailing
                edge of the trigger (after the label) so the committed 30D chip/trigger
                stays visually distinct from the in-flight Custom draft. Visible at every
                viewport without expanding the touch target (8px dot inside a 44px-min
                button). Renders only when hasDraft, so the no-draft state is unchanged. */}
            {hasDraft && (
              <span
                aria-hidden="true"
                data-testid="transaction-date-range-trigger-draft-dot"
                className="ml-1 inline-block h-2 w-2 shrink-0 self-center rounded-full bg-amber-500 ring-2 ring-amber-500/25"
              />
            )}

          </Button>
        </PopoverTrigger>
        <PopoverContent
          ref={popoverContentRef}
          className={cn(
            'z-[70] flex flex-col overflow-hidden p-2 md:p-1',
            'max-h-[min(44rem,var(--radix-popover-content-available-height))]',
            // [AC5] Width selection. When the parent supplied container metrics
            // (`popoverLayout` is set at open time), use exactly that width via
            // inline style — the inline style wins over any class. Otherwise
            // fall back to the legacy `w-[min(48rem,calc(100vw-2rem))]` cap so
            // the popover never exceeds the viewport on any host.
            popoverLayout ? undefined : isMinimized
              ? 'w-[min(48rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)]'
              : 'w-[min(48rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)]',
          )}
          style={popoverLayout ? { width: `${popoverLayout.width}px` } : undefined}
          align="start"
          alignOffset={popoverLayout?.offset}
          sideOffset={6}
          collisionPadding={8}
          data-testid="transaction-date-range-popover"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            className={cn(
              // [RANGE_VISUAL_ITERATION_15] Relative surface with the preset rail
              // absolutely anchored on the right, so the popover sizes to the
              // section's natural content (Start/End, calendar, actions) and
              // the 14-control rail's intrinsic height cannot stretch it. The
              // surface reserves the rail's width via responsive right padding
              // so content never sits under the rail at any viewport.
              //
// Surface max-h deducts the popover's padding+border overhead
// (p-2 md:p-1, 1px border all around) from the radix available
// height: 18px at <md, 10px at md+. The calendar wrapper uses
// `min-h-0 sm:max-h-[21.5rem]` with `overflow-y-auto` so it
// shrinks below the 344px natural grid height whenever the
// section's available height is less than the sum of Start/End
// + banner + calendar + actions + gaps (the cycle-11 desktop
// case: 1440x900 lands the trigger near y=428 so the popover
// max-h resolves to 457px and the section's intrinsic 481px
// pushes Cancel/Apply 33px below the popover bottom). The
// `max-h` cap preserves the 6-row grid at roomy viewports
// (540x1170 / 768x1024) where available space exceeds 344px,
// while cramped viewports scroll the grid internally and keep
// Cancel/Apply anchored inside the popover.
              //
              // [RANGE_VISUAL_ITERATION_15] Action row uses `pt-2 md:pt-1` —
              // mobile keeps 8px of breathing room above the divider for
              // touch-target readability, while the desktop 4px frees the
              // exact ~4px the 1440x900 final row needs to fit naturally.

              'relative flex w-full min-h-0 flex-col overflow-hidden max-h-[calc(var(--radix-popover-content-available-height)-1.125rem)] md:max-h-[calc(var(--radix-popover-content-available-height)-0.625rem)] pr-[7.25rem] sm:pr-[9rem] md:pr-[16rem]',
            )}
            data-testid="transaction-date-range-surface"
          >
            <section className="flex h-full min-h-0 min-w-0 flex-col gap-1" aria-label="Choose a date range">
              <div className="flex shrink-0 items-stretch gap-2 text-xs">
                <div
                  className="min-w-0 flex-1 rounded-lg border border-border bg-card px-3 py-2 md:py-1.5"
                  data-testid="transaction-date-range-start"
                >
                  <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.12em]">
                    Start
                  </div>
                  <div
                    className={cn(
                      // [AC7 mobile] Drop `truncate` and switch to text-[10px] +
                      // break-words + leading-tight on <md so the full
                      // `EEE MMM d, yyyy` string (e.g. "Mon Jan 5, 2026")
                      // fits inside the 108px-wide Start/End card without
                      // ellipsis. At >=md the cards widen to ~231px so
                      // text-xs (12px) + truncate remains safe.
                      // [AC7 mobile + tablet] Drop `md:truncate` so the All/Start/End value
                      // wraps inside its card instead of clipping to ellipsis. The
                      // `EEE MMM d, yyyy` formatted string (e.g. "Mon Jan 5, 2026")
                      // measures ~89px at text-xs — fits on one line inside the ~384px
                      // desktop card, but at 768 the section-constrained cards are
                      // ~74px wide (rail reserves 240px of the 422px popover), so the
                      // text needs to wrap to two lines to remain fully visible.
                      // `break-words` is already in the base class so wrapping is
                      // automatic; the only removal is the `md:truncate` override.
                      'break-words font-medium leading-tight text-[10px] md:text-xs md:leading-normal',
                      displayRange?.from || isAllRange ? 'text-foreground' : 'text-muted-foreground',
                    )}
                    data-testid="transaction-date-range-start-value"
                  >
                    {displayRange?.from
                      ? format(displayRange.from, MONTH_DAY_YEAR)
                      : allRangeDates?.from
                        ? format(allRangeDates.from, ALL_RANGE_DATE)
                        : isAllRange
                          ? ALL_DATES_LABEL
                          : 'Choose a start date'}
                  </div>
                </div>
                <div
                  className="min-w-0 flex-1 rounded-lg border border-border bg-card px-2 py-2 md:px-3 md:py-1.5"
                  data-testid="transaction-date-range-end"
                >
                  <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.12em]">
                    End
                  </div>
                  <div
                    className={cn(
                      // [AC7 mobile + tablet] Mirror the Start value above — see that
                      // block's comment for the full reasoning. Same drop of `md:truncate`
                      // so the End value stays visually complete at tablet and below.
                      'break-words font-medium leading-tight text-[10px] md:text-xs md:leading-normal',
                      displayRange?.to || isAllRange ? 'text-foreground' : 'text-muted-foreground',
                    )}
                    data-testid="transaction-date-range-end-value"
                  >
                    {displayRange?.to
                      ? format(displayRange.to, MONTH_DAY_YEAR)
                      : allRangeDates?.to
                        ? format(allRangeDates.to, ALL_RANGE_DATE)
                        : isAllRange
                          ? ALL_DATES_LABEL
                          : 'Choose an end date'}
                  </div>
                </div>
              </div>
              {/* [RANGE_VISUAL_ITERATION_18] Draft-pending banner. Sits between
                  Start/End cards (which now show the in-flight draft via iter17's
                  displayRange contract) and the calendar so the user reads the
                  banner in the same visual pass as the draft values it explains.
                  Compact single-line pill (≤32px tall at 11px text + py-1.5) so the
                  calendar shrinks by an equivalent amount on shorter surfaces and
                  stays at sm:min-h-[21.5rem] on >=sm. role=status + aria-live=polite
                  announces the state change to assistive tech without stealing focus.
                  The banner disappears the moment the popover closes (draft discarded)
                  or Apply commits, preserving Apply-only commit semantics. */}
              {hasDraft && (
                <div
                  className="flex shrink-0 items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] font-medium text-amber-800 dark:text-amber-200"
                  role="status"
                  aria-live="polite"
                  data-testid="transaction-date-range-draft-banner"
                >
                  <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                  <span>Draft range — press Apply to commit</span>
                </div>
              )}

              <div
                ref={setCalendarScrollEl}
                className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain touch-pan-y rounded-lg border border-border bg-card p-0 sm:max-h-[21.5rem] md:p-1.5 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-10 after:bg-gradient-to-t after:from-card after:to-transparent after:content-[''] data-[scrollable=false]:after:hidden"
                data-scrollable={calendarHasMoreBelow ? 'true' : 'false'}
                data-testid="transaction-date-range-calendar"
              >
<Calendar
mode="range"
selected={draft}
onSelect={handleCalendarSelect}
                  autoFocus
                  // [RANGE_VISUAL_ITERATION_31] Restore readable weekday/date glyphs at 320 and
                  // tablet while keeping all 7 columns fully inside the calendar wrapper. The
                  // shared `calendar.tsx` primitive renders the DayPicker root with `p-3 relative`,
                  // which steals 24px of horizontal room at every viewport. At 320x844 the calendar
                  // wrapper is 128px (surface reserves 116px right for the rail), so removing the
                  // horizontal padding gives the table 126px instead of 102px (7 × 18px cells vs
                  // 7 × 14.58px cells). At 768x1024 the wrapper is 156px (md:p-1.5 reserves 12px +
                  // rdp-root p-3 reserves 24px + months sm:space-x-4 wastes 16px on a single-month
                  // layout = 52px stolen); removing the horizontal padding AND the wasted space-x-4
                  // recovers 40px → table 142px (7 × 20.29px cells vs 7 × 14.58px cells). The vertical
                  // padding (p-3 top/bottom) is preserved so the month caption and last week row keep
                  // their breathing room. `!important` is required because the primitive's `p-3`
                  // shorthand would otherwise win over the responsive `px-0` override at the same
                  // specificity.
                  className="!px-0 md:!px-1"

                  // [RANGE_VISUAL_ITERATION_12] Distribute weekday headers and day cells
                  // across the calendar's full inner width while keeping each day button
                  // at a usable 36px and centered. The shared `calendar.tsx` primitive fixes
                  // every `weekday` and `day` at `w-9`, which leaves a fixed 252px-wide island of
                  // date columns even when the calendar wrapper is 418px wide (e.g. 540x1170) and
                  // can clip the Saturday column on narrow devices where the wrapper's intrinsic
                  // scrollWidth exceeds its clientWidth (e.g. 360 with scrollWidth 264 vs
                  // clientWidth 236). `flex-1` on `weekday` + `day` makes each cell claim an equal
                  // 1/7 share of the wrapper width; the inner `day_button` keeps `w-9 h-9` (36px)
                  // as the cap, with `mx-auto` centering it inside the cell and `max-w-full` capping
                  // the button to its cell when the cell shrinks below 36px (e.g. 360 viewport where
                  // 7 cells fit ~34px each) so the wrapper's scrollWidth never exceeds clientWidth + 1
                  // and the Saturday column is no longer clipped. Selected/range styling survives —
                  // the range background paints on the wider `day` cell so the highlight expands to
                  // fill the cell (the previous iteration-11 visual).
                  classNames={{
                    // Force the months + month flex containers to fill the calendar
                    // wrapper width so the `month_grid` table can claim 100% via its
                    // own `w-full`. Without these overrides the table collapses to
                    // its intrinsic 252px content width (7 × 36px cells) and the
                    // `flex-1` cells are no-ops because the parent `<tr>` is only
                    // 252px wide. `min-w-0` keeps the flex children shrink-safe so a
                    // future two-month calendar still distributes correctly.
                    months:
                      // [RANGE_VISUAL_ITERATION_31] Remove `sm:space-x-4` — with only one month
                      // displayed at every viewport, the 16px left margin steals 16px from the
                      // month width at ≥sm (e.g. 768x1024 reclaims 118px → 134px for the table).
                      // The flex-row direction is kept so a future two-month layout still distributes.
                      'flex w-full flex-col sm:flex-row sm:space-y-0',
                    month: 'min-w-0 flex-1 space-y-2',
// [RANGE_VISUAL_ITERATION_31] Pin the calendar table to the parent width with `table-fixed` so
                    // the 7 `<td>` cells distribute evenly via the existing `flex-1` (the `<tr>` has
                    // `display: flex` from the local `week: 'flex w-full mt-0'` override). Without
                    // `table-fixed`, the browser-default `table-layout: auto` sizes the table to
                    // its content (7 × ~17.66px ≈ 123.56px) which overflows the 102px `month` flex
                    // container — the wrapper's `overflow-x: hidden` then clips ~8.56px of the
                    // Saturday column at the 320x844 wrapper boundary. The new `!px-0 md:!px-1`
                    // className on the Calendar primitive + the `sm:space-x-4` removal on `months`
                    // give the table 126px (320x844) and 134px (768x1024) instead of 102px, so each
                    // cell becomes ~18px (320) or ~19px (768) instead of the unreadable ~14.58px
                    // that made weekday/date glyphs merge. `min-w-0` matches the shrink-safe contract
                    // already on `month` / `weekday` / `day`. `border-collapse` and `space-y-1`
                    // are preserved from the primitive — the vertical gap between `<thead>` and
                    // `<tbody>` is controlled by `space-y-1`.
                    month_grid: 'w-full min-w-0 table-fixed border-collapse space-y-1',
                    weekdays: 'flex w-full',
                    weekday:
                      'min-w-0 flex-1 rounded-md text-center text-muted-foreground font-normal text-[0.8rem]',
                    // [RANGE_VISUAL_ITERATION_28] mt-1 (was mt-2) tightens the gap between
// week rows from 8px to 4px, shaving 20px off the natural 6-row grid
// (5 × 4px). At 1440x900 the calendar wrapper's flex-1 height (304px in the
// draft state) was 4-20px shy of the previous 308px natural grid, so the
// sixth row (Aug 30, 31) was clipped by the wrapper's overflow-y-auto with
// no visible scrollbar — the after: gradient faded onto content that was
// already fully hidden, so it could not function as a scroll affordance.
// With mt-1 the natural grid becomes 288px and fits the 290px available
// area at 1440 DRAFT, exposing Aug 30/31/Sep 1-5 without scrolling.
// day_button keeps h-9 (36px) so the 44px touch-target guideline is
// preserved (36px is the calendar primitive's baseline; the popover's
// 44px targets live on the trigger and preset chips). At 390 DRAFT the
// calendar still scrolls by ~25px (mobile pattern); the wrapper's internal
// overflow-y-auto + 40px after: gradient remain in place. No changes to
// day_button, weekdays, week, or any test selector.
                    // [RANGE_VISUAL_ITERATION_30] mt-0 (was mt-1) tightens the gap between week rows
// from 4px to 0px, recovering another 20px off the 6-row August 2026 grid (5 × 4px).
// Combined with iter-28's mt-1 (from mt-2) the week-row gap has been reduced 8px → 4px → 0px
// (40px total recovered from the original 340px natural grid → 300px). Together with
// space-y-2 on month (saves 8px between caption and grid) and gap-1 on the section
// (saves 12px across the 3 internal gaps), the calendar wrapper now fits the natural
// 6-row August grid at 1440x900 with ~7px slack (was 33px short per cycle-30 probe),
// so Aug 30, 31 + Sep 1–5 are visible without internal scrolling and the after: gradient
// affordance no longer fires as a 'more below' signal in the desktop draft state.
// day_button keeps h-9 (36px) so the 44px touch-target guideline is preserved
// (36px is the calendar primitive's baseline; the popover's 44px targets live on
// the trigger and preset chips). At 390 mobile the wrapper still scrolls by ~20px
// (mobile pattern unchanged); the wrapper's internal overflow-y-auto + 40px after:
// gradient remain in place. No changes to day_button, weekdays, day, or any test
// selector.
                    week: 'flex w-full mt-0',
                    day:
                      'min-w-0 flex-1 h-9 p-0 relative text-center text-sm [&:has([aria-selected].day-range-end)]:rounded-r-lg [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg focus-within:relative focus-within:z-20',
                    day_button: cn(
                      buttonVariants({ variant: 'ghost' }),
                      // [RANGE_VISUAL_ITERATION_13] `w-full max-w-9` (was `w-9 max-w-full`) plus
                      // `min-w-0` on the flex weekday/day cells so a narrow wrapper (e.g. 360x780 where
                      // the inner client area is 236px and each cell claims ~33.7px) lets the cell SHRINK
                      // below the button's previous 36px intrinsic minimum. The previous `w-9 max-w-full`
                      // pinned day_button at 36px (buttonVariants already sets w-9; `max-w-full` was a no-op),
                      // which forced the cell's intrinsic min-width to 36px and the 7-cell row to 252px —
                      // exceeding the 236px wrapper and clipping Saturday. `w-full` makes the button fill its
                      // cell; `max-w-9` caps it at 36px when the cell is wider (>=390px viewport where each
                      // cell is >=54px), so the 36px cap behavior at 390/540/768/1440 is preserved with the
                      // button still centered via `mx-auto`. `min-w-0` on the flex cells disables the implicit
                      // `min-width: auto` so they can actually shrink to fit the wrapper.
                      'h-9 w-full max-w-9 p-0 font-normal mx-auto aria-selected:opacity-100',
                    ),
                  }}
                />
              </div>
              {/* [T-CalendarRail-Redesign-FooterWidth] Footer action row spans the FULL
                  surface width (escapes the rail reservation via a positive width extension
                  matching the surface's pr-[4.5rem] sm:pr-[8rem] md:pr-[15rem]) so All +
                  Custom dates and Cancel + Apply share the same baseline y-row at every
                  viewport. At 390px the previous cycle confined the controls to the 268px
                  section content box (surface minus 72px rail reservation) and pushed the
                  right group below the left group; with the extension the footer reaches the
                  surface's right edge and overlays the rail only at the bottom action band
                  via z-[2] above the rail's z-[1] plus bg-popover, so footer hit-testing
                  resolves to its buttons. Inner gaps and horizontal button padding drop at
                  <sm (gap-x-1.5 + px-2 on All/Custom + px-3 on Cancel/Apply) so all four
                  controls fit on a single row at 390x844 with readable labels and 44px
                  touch targets; md+ retains the roomier padding/gap. flex-wrap is kept as
                  a graceful fallback for any future tighter viewport. Rail selectors, 12
                  ranged presets, scroll-edge affordance, Apply gating, Cancel handler, and
                  every existing test ID (period-pill-all, period-pill-custom,
                  transaction-date-range-cancel, transaction-date-range-apply) are unchanged. */}
              <div className="relative z-[2] w-[calc(100%+7.25rem)] sm:w-[calc(100%+9rem)] md:w-[calc(100%+16rem)] flex shrink-0 flex-wrap items-center justify-between gap-x-1.5 gap-y-2 border-t border-border bg-popover pt-2 md:gap-x-2 md:pt-1">
                <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                  <Button
                    type="button"
                    ref={value.period === 'all' ? activePresetRef : undefined}
                    variant={value.period === 'all' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'h-11 min-h-[44px] min-w-[44px] justify-start gap-1.5 px-2 text-xs font-medium md:h-9 md:min-h-0 md:px-3',
                      value.period !== 'all' && 'border border-border/70 bg-muted/40 rounded-md',
                      value.period === 'all' && !hasDraft && 'border border-primary/60 bg-primary text-primary-foreground hover:bg-primary/90',
                      value.period === 'all' && hasDraft && 'border border-border/70 bg-muted/40 rounded-md text-foreground',
                    )}
                    data-testid={`${presetTestIdPrefix}all`}
                    data-period="all"
                    data-selected={value.period === 'all' ? 'true' : 'false'}
                    aria-pressed={value.period === 'all'}
                    onClick={() => handlePresetClick('all')}
                  >
                    <span className="truncate">All</span>
                    {value.period === 'all' && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </Button>
                  <Button
                    type="button"
                    ref={value.period === 'custom' ? activePresetRef : undefined}
                    variant={value.period === 'custom' ? 'default' : 'outline'}
                    size="sm"
                    aria-label="Custom dates"
                    className={cn(
                      'h-11 min-h-[44px] min-w-[44px] justify-start gap-1.5 px-2 text-xs font-medium md:h-9 md:min-h-0 md:px-3',
                      value.period !== 'custom' && 'border border-border/70 bg-muted/40 rounded-md',
                      value.period === 'custom' && !hasDraft && 'border border-primary/60 bg-primary text-primary-foreground hover:bg-primary/90',
                      value.period === 'custom' && hasDraft && 'border border-border/70 bg-muted/40 rounded-md text-foreground',
                    )}
                    data-testid={customTestId}
                    data-selected={value.period === 'custom' ? 'true' : 'false'}
                    aria-pressed={value.period === 'custom'}
                    onClick={() => handlePresetClick('custom')}
                  >
                    <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Custom dates</span>
                    {value.period === 'custom' && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 min-h-[44px] px-3 text-xs md:h-9 md:min-h-0 md:px-4"
                    data-testid="transaction-date-range-cancel"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-11 min-h-[44px] px-3 text-xs md:h-9 md:min-h-0 md:px-4"
                    data-testid="transaction-date-range-apply"
                    disabled={!canApply}
                    onClick={handleApply}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </section>

            <aside
              className={cn(
                // [RANGE_VISUAL_ITERATION_10+11] Absolutely anchored right rail:
                // detached from the calendar column's height so the rail's intrinsic
                // height can no longer stretch the popover. The rail fills the
                // reserved band and scrolls internally if its 12 chips overflow.
                //
                // [T-RAIL-SIDEBAR-EDGE] Three changes here:
                //  1. `bottom-14 md:bottom-11` (and no more `h-full`) reserves the
                //     shared action row — measured 53px at <md, 41px at md+ — so the
                //     rail scrollport ENDS above the footer and its bottom cue is
                //     visible immediately above it. Previously the rail ran to the
                //     surface bottom and the footer covered its last 41-53px: at
                //     390x844 the 1M/3M/6M/30D/1Y chips sat under the action row and
                //     a hit-test on 1Y resolved to the footer, not the chip.
                //  2. The rail is 108px at <sm (was 72px) so its chips can sit two to
                //     a row and still clear the 44px target; the calendar shrinks
                //     safely because every cell is `min-w-0` + `max-w-9`.
                //  3. The divider is present at EVERY breakpoint (mobile could not
                //     rely on `md:border-l`) and is 2px wide, with a 4-8px gutter
                //     between it and the calendar card's own 1px border so the two
                //     cannot read as one blended line. It uses the shared
                //     `border-border` token for consistent surface separation.
                'absolute right-0 top-0 bottom-14 md:bottom-11 z-[1] flex min-h-0 min-w-0 flex-col gap-1.5 pl-1 md:gap-2 md:pl-2',
                'w-[6.75rem] sm:w-[8rem] md:w-[15rem]',
                'border-l-2 border-border',
              )}
              data-testid="transaction-date-range-presets"
              aria-label="Quick date ranges"
            >
              {/* [RANGE_VISUAL_ITERATION_7] Intro block stays outside the scrollport so
                 it remains readable at every viewport without occluding chips. Compact
                 mobile typography gives way to the standard title size at md+.

                 [T-RAIL-SIDEBAR-EDGE] Moved OUT of the scrollport, where it used to be
                 `sticky top-0 z-10`. As a sticky overlay it occluded the first chips —
                 hit-tests on 1D and 2D resolved to this block's paragraph at both 768
                 and 1440 — and its z-10 also sat above the fade layer. Outside the
                 scrollport it is always visible, never overlaps a chip, and the chips
                 no longer need a `scroll-mt` big enough to clear it. */}
              <div className="shrink-0 px-2 pt-1">
                <div className="text-xs font-semibold leading-4 text-foreground md:text-sm">Quick ranges</div>
                {/* [T-RAIL-SIDEBAR-EDGE] The `mt-0.5` gap is written through the
                    `[&:not(:first-child)]` variant because index.css sets
                    `p:not(:first-child){margin-top:1.5rem}` in @layer base — (0,1,1)
                    specificity, which a plain `.mt-0.5` (0,1,0) loses to regardless of
                    layer order. Measured: this header block was 80px tall, 24px of it
                    that stray margin, and now that it sits outside the scrollport every
                    one of those pixels came off the rail's visible height. */}
                <p className="break-words text-xs leading-4 text-muted-foreground [&:not(:first-child)]:mt-0.5 md:leading-5">Jump to a preset or choose exact dates.</p>
              </div>
              {/* Non-scrolling fade wrapper. The CSS `scroll-edge-affordance`
class is intended for index.css; we render the same fade here via
Tailwind pseudo-element utilities so the rail overflow is always
visible (top fade when scrolled, bottom fade when more below). The
wrapper stays `relative overflow-hidden` so the pseudo-elements are
pinned to the visible edges (they would otherwise scroll with the
content inside a scrollport). `from-popover` matches the parent
popover background so the fade blends into the rail's empty area. */}
<div
ref={railScrollEdgeWrapperRef}
className={cn(
'relative flex min-h-0 min-w-0 flex-1 overflow-hidden',
"before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-6 before:bg-gradient-to-b before:from-popover before:to-transparent before:opacity-0 before:transition-opacity before:content-['']",
'data-[scroll-up=true]:before:opacity-100',
"after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-6 after:bg-gradient-to-t after:from-popover after:to-transparent after:opacity-0 after:transition-opacity after:content-['']",
'data-[scroll-down=true]:after:opacity-100',
)}
data-testid="transaction-date-range-preset-viewport"
>
              <div
                ref={railScrollEdgeRef}
                // `pb-3` is scroll padding, not reserved height: it gives the last chip
                // 12px of air above the action row (measured clearance went 7.19px →
                // 15.19px, which two independent visual passes read as flush before)
                // without taking a single pixel off the rail's visible height.
                className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto overscroll-contain touch-pan-y scrollbar-elegant pt-3 pb-3 md:gap-3 md:pt-2"
                data-testid="transaction-date-range-preset-scroll"
              >
              {PRESET_GROUPS.map((group) => (
                <section key={group.label} className="flex shrink-0 flex-col gap-1.5" aria-label={group.label}>
                  <h3 className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground md:hidden">
                    {group.label}
                  </h3>
                  <div
                    // [AC6] Two columns below md, three at md, and four at lg keep the
                    // ranged presets compact while preserving the 44px touch-target floor.
                    className="grid grid-cols-2 gap-1.5 md:grid-cols-3 md:gap-1.5 lg:grid-cols-4"
                    // previous `<md` single column put one 64px chip per row inside a
                    // 72px rail: 12 rows of wasted width and a 588px scrollHeight in a
                    // 406px port at 390x844. With the 108px rail each column measures
                    // ~46px, so the 44px target still holds and the rail scrolls less.

                    data-testid={`date-range-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {group.periods.map((period) => {
                      const isActive = value.period === period;
                      return (
                        <Button
                        key={period}
                        type="button"
                        ref={isActive ? activePresetRef : undefined}
                        variant={isActive ? 'default' : 'ghost'}
                          size="sm"
                          className={cn(
                            // [T-RAIL-SIDEBAR-EDGE] Scroll margins now equal the 24px
                            // fade band (`--edge-fade-size`) instead of clearing the
                            // retired sticky header (64px) and the retired pinned
                            // "Other" footer (94/145px). The iter-6 active-chip
                            // `scrollIntoView({block:'nearest'})` therefore parks a chip
                            // clear of both cues, and nothing overlays the rail anymore:
                            // the header moved out of the scrollport and the shared
                            // action row is reserved through the aside's `bottom-14
                            // md:bottom-11`.
                            'scroll-mt-6 scroll-mb-6',
                            // Center the label below md, where columns are narrow; return
                            // to left alignment at md+ where the rail columns have room.
                            'min-h-[44px] min-w-[44px] w-full justify-center gap-1.5 px-2 text-xs font-medium md:justify-start md:px-3',
                            // [AC3/AC4] Keep every chip outlined at every breakpoint. Inactive
                            // and draft states use the neutral border token; the committed
                            // active state adds a primary outline over the primary fill.
                            !isActive && 'border border-border/70 bg-muted/40 rounded-md',
                            // [RANGE_VISUAL_ITERATION_23] A pending Custom draft keeps the
                            // committed chip in the neutral palette; the banner, trigger dot,
                            // Start/End cards, and Apply state carry the draft signal.
                            isActive && !hasDraft && 'border border-primary/60 bg-primary text-primary-foreground hover:bg-primary/90',
                            isActive && hasDraft && 'border border-border/70 bg-muted/40 rounded-md text-foreground',
                          )}
                          data-testid={`${presetTestIdPrefix}${period}`}
                          data-period={period}
                          data-selected={isActive ? 'true' : 'false'}
                          aria-pressed={isActive}
                          onClick={() => handlePresetClick(period)}
                        >
                          <span className="truncate">{PERIOD_LABELS[period]}</span>
                          {isActive && (
                            <Check
                              // [T-RAIL-SIDEBAR-EDGE] md+ only. A 46px <md column cannot
                              // fit label + check without truncating the label, and
                              // truncation must never hide a range label. At <md the
                              // committed chip is carried by the primary fill; while a
                              // draft is in flight (which neutralises that fill per
                              // iter-23) the draft banner, trigger dot, Start/End cards
                              // and the enabled Apply carry the state. `data-selected`
                              // and `aria-pressed` stay on every breakpoint for AT and
                              // for the existing tests.
                              className="hidden h-3.5 w-3.5 shrink-0 md:ml-auto md:block"
                            />
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </section>
              ))}
              {/* [T-CalendarRail-Redesign] `All` and `Custom dates` moved out of the rail to the
                  shared bottom action row (alongside Cancel + Apply), so the rail is now exactly
                  the 12 ranged preset chips. The iter-19 standalone bottom-fade and the iter-25
                  pinned "Other" footer are both retired — both rail cues come from the shared
                  scroll-edge affordance on the wrapper above (measured data-scroll-up/down), so we
                  never paint a cue over fully-visible content and never detach it from the edge. */}
                </div>
              </div>
            </aside>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
